<template>
  <button
    class="card"
    :class="{ '--compact': compact, '--rich': !compact, '--in-fav': isInBookmarksNow }"
    type="button"
    :data-slide-id="slide.id"
    @click="handleSelect"
  >
    <template v-if="compact">
      <span class="card-icon-flat">
        <span class="material-symbols-rounded">{{ slide.icon }}</span>
      </span>
      <span class="card-name-cell">
        <span class="card-name">{{ slide.name }}</span>
        <span
          v-if="showNewDot"
          class="card-new-tag"
          aria-label="New slide type"
          title="New slide type"
        ></span>
      </span>
      <span
        class="card-type-chip"
        :class="`card-type-chip--${slide.sectionKey}`"
        :title="typeChipTitle"
      >{{ slide.sectionLabel }}</span>
    </template>

    <template v-else>
      <!-- Hero art band — tinted with the slide's accent so each card
           reads as a distinct visual object across the grid. -->
      <span class="card-art" :style="{ '--accent': slide.accent }">
        <span v-if="slide.isNew" class="card-new-badge">NEW</span>
        <!-- Bookmark toggle floats over the art band's top-right
             corner. Filled + pink when the slide is in My Bookmarks. -->
        <span
          class="card-fav"
          :class="{ '--active': isInBookmarksNow }"
          role="button"
          tabindex="0"
          :aria-label="isInBookmarksNow ? `Remove ${slide.name} from My Bookmarks` : `Add ${slide.name} to My Bookmarks`"
          :aria-pressed="isInBookmarksNow.toString()"
          :title="isInBookmarksNow ? 'Remove from My Bookmarks' : 'Add to My Bookmarks'"
          @click.stop="onToggleBookmark"
          @keydown.enter.stop.prevent="onToggleBookmark"
          @keydown.space.stop.prevent="onToggleBookmark"
        >
          <transition name="card-fav-icon" mode="out-in">
            <span
              :key="isInBookmarksNow ? 'on' : 'off'"
              class="material-symbols-rounded"
              :class="{ filled: isInBookmarksNow }"
            >{{ isInBookmarksNow ? 'bookmark' : 'bookmark_border' }}</span>
          </transition>
        </span>
        <span class="card-art-icon">
          <span class="material-symbols-rounded filled">{{ slide.icon }}</span>
        </span>
        <span
          class="card-art-chip"
          :class="`card-art-chip--${slide.sectionKey}`"
          :title="typeChipTitle"
        >{{ slide.sectionLabel }}</span>
      </span>

      <span class="card-content">
        <span class="card-title">{{ slide.name }}</span>
        <span class="card-desc">{{ slide.tagline || slide.desc }}</span>
        <button
          v-if="withQuickAdd"
          type="button"
          class="card-add"
          :data-testid="`card-add-${slide.id}`"
          :aria-label="`Add ${slide.name} to deck`"
          @click.stop="onQuickAdd"
        >
          <span class="material-symbols-rounded">add</span>
          <span class="card-add-label">Add to deck</span>
        </button>
      </span>
    </template>
  </button>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import { FREE_PLAN_LIMITS, type SlideType } from '../data/slideTypeCatalog'
import {
  isInPicker, addSlideToPicker, removeSlideFromPicker,
  isNewlyAdded, clearNewlyAdded,
} from '../lib/pinnedStore'
import { confirmRemoveFromPicker } from '../lib/confirmStore'
import { showToast } from '../lib/toastStore'

export default Vue.extend({
  name: 'SlideTypeCard',
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
    compact: { type: Boolean, default: false },
    withQuickAdd: { type: Boolean, default: false },
  },
  computed: {
    isInBookmarksNow(): boolean {
      return isInPicker(this.slide)
    },
    showNewDot(): boolean {
      // Pink dot signals "new to me" — slides the user just added via
      // the marketplace's bookmark toggle. Catalog-flagged isNew is the
      // marketplace's concern (NEW badge there), not the picker's.
      return isNewlyAdded(this.slide.id)
    },
    typeChipTitle(): string {
      const limit = FREE_PLAN_LIMITS[this.slide.sectionKey]
      if (limit === null) return `${this.slide.sectionLabel} · unlimited on Free plan`
      return `${this.slide.sectionLabel} · ${limit} per deck on Free plan`
    },
  },
  methods: {
    async onToggleBookmark() {
      if (this.isInBookmarksNow) {
        // Removal is destructive — confirm before unbookmarking. Icon
        // stays filled while the dialog is open; toggles only after
        // the user confirms.
        const ok = await confirmRemoveFromPicker(this.slide.name)
        if (!ok) return
        removeSlideFromPicker(this.slide)
        showToast({
          before: 'Removed',
          emphasis: this.slide.name,
          after: 'from My Bookmarks',
          icon: 'bookmark_remove',
          iconColor: 'var(--aha-error)',
        })
      } else {
        // Adding is non-destructive — flip immediately, announce in toast.
        addSlideToPicker(this.slide)
        showToast({
          before: 'Added',
          emphasis: this.slide.name,
          after: 'to My Bookmarks',
          icon: 'bookmark',
          iconColor: 'var(--aha-pink)',
        })
      }
    },
    handleSelect() {
      clearNewlyAdded(this.slide.id)
      this.$emit('select', this.slide)
    },
    onQuickAdd() {
      clearNewlyAdded(this.slide.id)
      this.$emit('quick-add', this.slide)
    },
  },
})
</script>

<style scoped lang="scss">
.card {
  --ease-out: cubic-bezier(0.23, 1, 0.32, 1);
  appearance: none;
  background: transparent;
  border: 1px solid transparent;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  color: inherit;
  width: 100%;
  outline: none;
  position: relative;
}

/* Compact: just icon | name | chip. No actions: management lives on
   the marketplace card's bookmark toggle (more space, more context). */
.card.--compact {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) auto;
  gap: 6px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1A1A2E;
  transition: background 160ms var(--ease-out), color 160ms var(--ease-out);
}
.card.--compact .card-icon-flat {
  width: 24px; height: 24px;
  display: grid; place-items: center;
  color: rgba(15, 15, 20, 0.6);
  transition: color 160ms var(--ease-out);
}
.card.--compact .card-icon-flat .material-symbols-rounded { font-size: 22px; }
.card.--compact .card-name-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.card.--compact .card-name {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.card.--compact:focus-visible {
  border-color: #C8AAFF;
  box-shadow: 0 0 0 2px rgba(211, 180, 255, 0.2);
}
@media (hover: hover) and (pointer: fine) {
  .card.--compact:hover {
    background: #F5EEFF;
  }
  .card.--compact:hover .card-name { color: #6A1EBB; }
  .card.--compact:hover .card-icon-flat { color: #B98EFF; }
  .card.--compact:hover .card-type-chip { display: none; }
}
.card.--compact:active { background: #ECE0FF; }
.card.--compact:active .card-name { color: #5A189A; }

.card-new-tag {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: var(--aha-pink);
  flex-shrink: 0;
}

/* Rich: marketplace grid card (icon + title row [name + bookmark] + desc + footer [chip + Add btn]). */
/* Rich card — split into a tinted hero art band (accent-coloured,
   shows the slide's icon big) and a content block (title, tagline,
   Add button). The art band gives the grid its visual variety;
   no two cards look the same at a glance. */
.card.--rich {
  position: relative;
  background: #ffffff;
  border: 1px solid rgba(15, 15, 20, 0.06);
  border-radius: 14px;
  overflow: hidden;
  padding: 0;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 0 rgba(15, 15, 20, 0.02);
  transition:
    transform 160ms var(--ease-out),
    box-shadow 160ms var(--ease-out),
    border-color 160ms var(--ease-out),
    background 160ms var(--ease-out);
}
.card.--rich:focus-visible {
  border-color: rgba(106, 30, 187, 0.5);
  box-shadow: 0 0 0 3px rgba(106, 30, 187, 0.18);
}
@media (hover: hover) and (pointer: fine) {
  .card.--rich:hover {
    transform: translateY(-2px);
    border-color: rgba(15, 15, 20, 0.12);
    box-shadow: 0 12px 28px -12px rgba(15, 15, 20, 0.22);
  }
}
.card.--rich:active { transform: scale(0.98); transition-duration: 100ms; }

/* ---------- Art band ---------- */
.card-art {
  position: relative;
  height: 96px;
  background:
    radial-gradient(120% 90% at 100% 0%, color-mix(in srgb, var(--accent) 24%, transparent), transparent 60%),
    radial-gradient(140% 100% at 0% 100%, color-mix(in srgb, var(--accent) 10%, transparent), transparent 65%),
    color-mix(in srgb, var(--accent) 12%, white);
  display: grid;
  place-items: center;
  border-bottom: 1px solid color-mix(in srgb, var(--accent) 18%, rgba(15, 15, 20, 0.04));
}
.card-art-icon {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  background: white;
  color: var(--accent);
  display: grid;
  place-items: center;
  box-shadow:
    0 1px 0 rgba(15, 15, 20, 0.03),
    0 12px 24px -8px color-mix(in srgb, var(--accent) 45%, transparent);
  transition: transform 200ms var(--ease-out);
}
.card-art-icon .material-symbols-rounded { font-size: 34px; }
@media (hover: hover) and (pointer: fine) {
  .card.--rich:hover .card-art-icon { transform: translateY(-2px) scale(1.02); }
}
.card-art-chip {
  position: absolute;
  bottom: 8px;
  left: 10px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 999px;
  white-space: nowrap;
  line-height: 1.3;
  background: white;
  color: var(--accent);
  box-shadow: 0 1px 0 rgba(15, 15, 20, 0.04);
}
.card-art-chip--quiz   { color: #6A1EBB; }
.card-art-chip--poll   { color: #0f7a5c; }
.card-art-chip--content { color: rgba(15, 15, 20, 0.65); }

/* ---------- Content block ---------- */
.card-content {
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  text-align: left;
}
.card.--rich .card-title {
  font-size: 15px;
  font-weight: 700;
  color: #1A1A2E;
  line-height: 1.3;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.card-desc {
  font-size: 12px;
  color: rgba(15, 15, 20, 0.6);
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-new-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.06em;
  background: var(--aha-pink);
  color: white;
  padding: 2px 7px;
  border-radius: 4px;
  z-index: 1;
}

.card-add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: auto;
  padding: 8px 12px;
  border: 0;
  background: rgba(106, 30, 187, 0.08);
  color: #6A1EBB;
  border-radius: 10px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition:
    background 160ms var(--ease-out),
    color 160ms var(--ease-out),
    transform 160ms var(--ease-out);
}
.card-add .material-symbols-rounded { font-size: 16px; }
.card-add:hover {
  background: var(--aha-purple);
  color: white;
  transform: translateY(-1px);
}
.card-add:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(106, 30, 187, 0.25);
}

/* Bookmark toggle — sits over the art band's top-right corner. White
   chip background + pink heart when bookmarked, neutral when not. */
.card-fav {
  --ease-out: cubic-bezier(0.23, 1, 0.32, 1);
  position: absolute;
  top: 8px;
  right: 8px;
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(4px);
  color: rgba(15, 15, 20, 0.55);
  cursor: pointer;
  z-index: 1;
  transition: background 160ms var(--ease-out), color 160ms var(--ease-out), transform 200ms var(--ease-out);
}
.card-fav .material-symbols-rounded { font-size: 20px; }
.card-fav:hover {
  background: rgba(255, 64, 129, 0.10);
  color: var(--aha-pink);
}
.card-fav:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px rgba(255, 64, 129, 0.35);
}
/* Active state: pink filled bookmark on the white chip background —
   reads clearly against the accent-tinted art band. */
.card-fav.--active {
  color: var(--aha-pink);
  background: white;
  box-shadow: 0 1px 4px rgba(255, 64, 129, 0.25);
}
.card-fav.--active:hover {
  background: white;
  color: var(--aha-error);
}

/* Bookmark icon swap animation. Quick scale + opacity so the state
   change reads at a glance (hollow ↔ filled). Out-in mode prevents both
   icons from overlapping mid-swap. */
.card-fav-icon-enter-active {
  transition: transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 160ms var(--ease-out);
}
.card-fav-icon-leave-active {
  transition: transform 140ms var(--ease-out), opacity 120ms var(--ease-out);
}
.card-fav-icon-enter-from, .card-fav-icon-enter {
  opacity: 0;
  transform: scale(0.4);
}
.card-fav-icon-leave-to {
  opacity: 0;
  transform: scale(0.7);
}

/* Lavender-tint flash after a slide is picked from the picker. */
.card.--flash {
  animation: card-flash-bg 1500ms cubic-bezier(0.23, 1, 0.32, 1) 1;
}
@keyframes card-flash-bg {
  0%, 35% { background: rgba(106, 30, 187, 0.08); }
  100% { background: transparent; }
}

@media (prefers-reduced-motion: reduce) {
  .card, .card-fav { transition-property: opacity, background, border-color, color; }
  .card.--rich:hover { transform: none; }
  .card:active { transform: none; }
  .card.--flash { animation: none; }
}
</style>

<!--
  Non-scoped chip styles — shared by every component that renders a
  .card-type-chip (picker compact card, marketplace card, etc).
-->
<style lang="scss">
.card-type-chip {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  line-height: 1.4;
  opacity: 0.95;
  transition: opacity 160ms cubic-bezier(0.23, 1, 0.32, 1);
}
.card-type-chip--quiz {
  background: rgba(106, 30, 187, 0.12);
  color: #6A1EBB;
}
.card-type-chip--poll {
  background: rgba(16, 185, 129, 0.16);
  color: #0f7a5c;
}
.card-type-chip--content {
  background: rgba(15, 15, 20, 0.06);
  color: rgba(15, 15, 20, 0.55);
}
</style>
